'use client';

import NumberFlow from '@number-flow/react';
import { useTranslations } from 'next-intl';
import { type ReactNode, type Ref, useEffect, useRef } from 'react';
import { Icon } from '@/lib/ui-kit/icons';
import { TokenIcon } from '@/lib/ui-kit/token-icon';
import { cn } from '@/lib/ui-kit/utils';
import { formatMaskedMoney, isBelowMoneyThreshold } from '@/lib/utils/formatAmount';
import { CurrencySelect, type CurrencySelectProps } from './CurrencySelect';

export type BalanceView = 'total' | 'available';

interface BalanceCardProps {
  amount: number;
  usdValue: number;
  currencyLabel: string;
  /** Figma Hero: a 20px coin before the figure on the product balances, none on Total Assets. */
  showCoinIcon: boolean;
  /** The "USDC ▾" picker; without it the ticker is plain text. */
  currencySelect?: CurrencySelectProps;
  fractionDigits: number;
  view: BalanceView;
  onViewChange: (view: BalanceView) => void;
  hidden: boolean;
  onOpenSettings: () => void;
  /** Where focus returns when "What to show" closes. */
  settingsButtonRef?: Ref<HTMLButtonElement>;
  walletLink: ReactNode;
}

const VIEW_ORDER: BalanceView[] = ['total', 'available'];
// Figma 14226:204177: Default secondary, Hover primary at 60%, Active primary.
const VIEW_LABEL_CLASS: Record<'active' | 'idle', string> = {
  active: 'text-v2-text-primary',
  idle: 'text-v2-text-secondary hover:text-v2-text-primary hover:opacity-60 focus-visible:text-v2-text-primary focus-visible:opacity-60',
};

/** Long balances step down a size so the figure never overflows the card. */
function valueSizeClass(text: string): string {
  if (text.length > 16) return 'text-[16px] leading-5';
  if (text.length > 12) return 'text-[20px] leading-7';
  return 'text-[24px] leading-8';
}

/** The popover's hero (Figma 14226:204194): Total | Available, the figure, Wallet Overview. */
export function BalanceCard({
  amount,
  usdValue,
  currencyLabel,
  showCoinIcon,
  currencySelect,
  fractionDigits,
  view,
  onViewChange,
  hidden,
  onOpenSettings,
  settingsButtonRef,
  walletLink,
}: BalanceCardProps) {
  const isFirstRender = useRef(true);
  const t = useTranslations('balancePopover');

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  const amountText = formatMaskedMoney(amount, hidden, { decimals: fractionDigits });
  const usdText = formatMaskedMoney(usdValue, hidden, { prefix: '≈$' });
  const handleTotal = () => onViewChange('total');
  const handleAvailable = () => onViewChange('available');
  const viewHandlers: Record<BalanceView, () => void> = {
    total: handleTotal,
    available: handleAvailable,
  };

  return (
    <div className="flex w-full flex-col gap-1 overflow-hidden rounded-[10px] bg-v2-neutral-surface dark:bg-white/10">
      <div className="flex items-center gap-3 px-6 pt-4">
        <div className="flex flex-1 items-center gap-2 text-[12px] leading-[1.15]">
          {VIEW_ORDER.map((option, index) => (
            <span key={option} className="flex items-center gap-2">
              {index > 0 ? <span aria-hidden className="h-3 w-px bg-v2-text-secondary" /> : null}
              <button
                type="button"
                onClick={viewHandlers[option]}
                aria-pressed={view === option}
                className={cn(
                  'cursor-pointer outline-none transition-[color,opacity]',
                  VIEW_LABEL_CLASS[view === option ? 'active' : 'idle'],
                )}
              >
                {t(option)}
              </button>
            </span>
          ))}
        </div>
        {/* Figma 14226:125659: grey-400 border → grey-300 on hover → + white 10% fill when pressed. */}
        <button
          ref={settingsButtonRef}
          type="button"
          onClick={onOpenSettings}
          aria-label={t('whatToShow')}
          className="flex cursor-pointer items-center rounded-[6px] border border-solid border-v2-btn-secondary-hover p-2 text-v2-text-primary outline-none transition-colors hover:border-v2-text-secondary focus-visible:border-v2-text-secondary active:border-v2-text-secondary active:bg-v2-surface-tertiary dark:active:bg-white/10"
        >
          <Icon name="Settings2" size={16} />
        </button>
      </div>

      <div className="flex flex-col gap-2 px-6 py-2">
        <div className="flex min-w-0 items-center gap-2 text-v2-text-primary">
          {showCoinIcon ? (
            <TokenIcon symbol={currencyLabel} size={20} className="shrink-0" />
          ) : null}
          <span
            className={cn(
              'ph-no-capture min-w-0 truncate font-semibold tabular-nums',
              valueSizeClass(amountText),
            )}
          >
            {hidden || isBelowMoneyThreshold(amount, fractionDigits) ? (
              amountText
            ) : (
              <NumberFlow
                value={amount}
                format={{
                  minimumFractionDigits: fractionDigits,
                  maximumFractionDigits: fractionDigits,
                }}
                locales="en-US"
                animated={!isFirstRender.current}
                willChange
              />
            )}
          </span>
          {currencySelect ? (
            <span className="flex shrink-0 self-stretch">
              <CurrencySelect {...currencySelect} />
            </span>
          ) : (
            <span className="shrink-0 self-end text-[15px] leading-[26px] text-v2-text-secondary">
              {currencyLabel}
            </span>
          )}
        </div>
        <span className="ph-no-capture text-[12px] leading-[1.5] tabular-nums text-v2-text-secondary">
          {hidden || isBelowMoneyThreshold(usdValue) ? (
            usdText
          ) : (
            <NumberFlow
              value={usdValue}
              prefix="≈$"
              format={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
              locales="en-US"
              animated={!isFirstRender.current}
              willChange
            />
          )}
        </span>
        <div className="h-[1.5px] w-full bg-v2-border dark:bg-white/10" />
        {walletLink}
      </div>
    </div>
  );
}
